/**
 * Login.js — phone/email OTP login screen.
 *
 * Two steps: identifier input → 6-digit OTP. Calls inbox-auth Netlify function.
 * On success, populates session store and navigates to chat-list.
 */

import { html } from 'htm/preact';
import { useState, useEffect, useRef } from 'preact/hooks';

import { setSession } from '../stores/session.js';
import * as api from '../services/api.js';
import { normalizePhone } from '../services/phone.js';
import { navigateAfterLogin } from '../services/permissions-check.js';

/**
 * Turn whatever the user typed into the canonical form the backend
 * expects. Emails → lowercase + trimmed. Phones → normalized through
 * the shared phone.js helper so 10-digit Indian numbers get the
 * country-code prefix (#118). Both handleSendOtp and handleVerifyOtp
 * use this so the cleaned value is always identical between the two
 * calls for the same login attempt — otherwise a user could send an
 * OTP keyed by one form of their number and fail to verify it keyed
 * by another.
 */
function _cleanIdentifier(raw) {
  const isEmail = raw.includes('@');
  const cleaned = isEmail ? raw.trim().toLowerCase() : normalizePhone(raw);
  return { isEmail, cleaned };
}

export function Login() {
  const [step, setStep]                 = useState('identifier'); // 'identifier' | 'otp'
  const [identifier, setIdentifier]     = useState('');
  // Default to phone — most agents log in with their WhatsApp number. On
  // mobile this controls inputMode (numeric keypad vs email keyboard with
  // @/. keys) so the keypad matches what the user is actually typing. A
  // toggle below the field flips to email mode for the minority case.
  const [idMode, setIdMode]             = useState('phone'); // 'phone' | 'email'
  const [otp, setOtp]                   = useState(['', '', '', '', '', '']);
  const [maskedId, setMaskedId]         = useState('');
  const [error, setError]               = useState(null);
  const [sending, setSending]           = useState(false);
  const [verifying, setVerifying]       = useState(false);
  const [secondsLeft, setSecondsLeft]   = useState(0);
  // Resend rate-limit cooldown — driven by the server's retry_after_sec on 429.
  // While >0 the resend button is disabled and shows an "available in mm:ss" hint.
  const [cooldownLeft, setCooldownLeft] = useState(0);

  const otpRefs = useRef([]);

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  // Countdown timer for resend rate-limit cooldown
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const t = setTimeout(() => setCooldownLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldownLeft]);

  // Toggle between phone and email modes. Clears the field so the value
  // can't leak across modes (`9876543210` left over from phone mode
  // shouldn't sit in an email input).
  const switchIdMode = () => {
    setIdMode(m => m === 'phone' ? 'email' : 'phone');
    setIdentifier('');
    setError(null);
  };

  const handleSendOtp = async () => {
    setError(null);
    if (cooldownLeft > 0) return; // belt-and-braces — button is also disabled
    if (!identifier.trim()) {
      setError(idMode === 'phone' ? 'Please enter your WhatsApp number' : 'Please enter your work email');
      return;
    }
    const { isEmail, cleaned } = _cleanIdentifier(identifier);

    setSending(true);
    try {
      const data = await api.sendOtp(cleaned, isEmail ? 'email' : 'phone');
      setMaskedId(data.masked || cleaned);
      setSecondsLeft(data.expires_in || 600);
      // Clear any previously-typed digits so a Resend always starts fresh
      // — otherwise stale digits from the old OTP would auto-submit and
      // fail with "Invalid OTP" the moment the new code lands.
      setOtp(['', '', '', '', '', '']);
      setStep('otp');
      // Focus first OTP box
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch (e) {
      // 429: server returns retry_after_sec. Start a visible countdown so
      // the user knows when they can try again instead of just bouncing
      // off the same error every tap.
      if (e.status === 429 && e.retry_after_sec) {
        setCooldownLeft(e.retry_after_sec);
      }
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const formatCooldown = () => {
    const m = Math.floor(cooldownLeft / 60);
    const s = cooldownLeft % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleOtpInput = (idx, value) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);
    const next = [...otp];
    next[idx] = digit;
    setOtp(next);

    if (digit && idx < 5) {
      otpRefs.current[idx + 1]?.focus();
    }

    // Auto-submit when all 6 filled
    if (next.every(d => d) && next.join('').length === 6) {
      handleVerifyOtp(next.join(''));
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = pasted.split('').concat(['', '', '', '', '', '']).slice(0, 6);
    setOtp(next);
    if (pasted.length === 6) handleVerifyOtp(pasted);
  };

  const handleVerifyOtp = async (code) => {
    if (verifying) return;
    setError(null);
    if (code.length < 6) {
      setError('Enter the full 6-digit OTP');
      return;
    }
    const { isEmail, cleaned } = _cleanIdentifier(identifier);

    setVerifying(true);
    try {
      const data = await api.verifyOtp(cleaned, code);
      setSession(data.accessToken, data.session);
      // #165: audit CRM role permissions before dropping into chat list.
      await navigateAfterLogin();
    } catch (e) {
      setError(e.message);
    } finally {
      setVerifying(false);
    }
  };

  const formatTimer = () => {
    const m = Math.floor(secondsLeft / 60);
    const s = secondsLeft % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return html`
    <div class="inbox-screen" style="display:flex">
      <div class="inbox-login-wrap">
        <div class="inbox-login-brand">
          <img src="/assets/images/logo-512.png" alt="ODIV" width="48" height="48" />
          <h1 class="inbox-login-title">ODIV Inbox</h1>
          <p class="inbox-login-sub">Sign in to handle WhatsApp conversations</p>
        </div>

        ${step === 'identifier' && html`
          <div class="inbox-auth-card">
            <div class="inbox-form-group">
              <div style="display:flex;align-items:baseline;justify-content:space-between;gap:8px;margin-bottom:6px">
                <label class="inbox-label" style="margin:0">
                  ${idMode === 'phone' ? 'WhatsApp number' : 'Work email'}
                </label>
                <button
                  type="button"
                  class="inbox-btn-link inbox-login-mode-toggle"
                  onClick=${switchIdMode}
                  style="font-size:0.74rem;background:transparent;border:none;color:var(--v2-accent,#25D366);cursor:pointer;padding:0;text-decoration:underline;text-underline-offset:2px"
                >
                  ${idMode === 'phone' ? 'Use email instead' : 'Use phone instead'}
                </button>
              </div>
              ${idMode === 'phone'
                ? html`<input
                    key="login-input-phone"
                    type="tel"
                    class="inbox-input"
                    placeholder="91 98765 43210"
                    autoComplete="tel"
                    inputMode="tel"
                    value=${identifier}
                    onInput=${(e) => setIdentifier(e.target.value)}
                    onKeyDown=${(e) => e.key === 'Enter' && handleSendOtp()}
                  />`
                : html`<input
                    key="login-input-email"
                    type="email"
                    class="inbox-input"
                    placeholder="you@company.com"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    spellcheck=${false}
                    value=${identifier}
                    onInput=${(e) => setIdentifier(e.target.value)}
                    onKeyDown=${(e) => e.key === 'Enter' && handleSendOtp()}
                  />`
              }
            </div>
            <div class="inbox-form-hint">
              ${idMode === 'phone'
                ? 'Enter your WhatsApp number with country code (no +)'
                : 'Enter the work email registered with your team'}
            </div>
            <button
              class="inbox-btn inbox-btn-primary"
              onClick=${handleSendOtp}
              disabled=${sending || cooldownLeft > 0}
            >
              <span>${
                cooldownLeft > 0
                  ? `Try again in ${formatCooldown()}`
                  : (sending ? 'Sending…' : 'Send OTP')
              }</span>
            </button>
            ${error && html`<div class="inbox-error">${error}</div>`}
          </div>
        `}

        ${step === 'otp' && html`
          <div class="inbox-auth-card">
            <p class="inbox-otp-sent-msg">OTP sent to <strong>${maskedId}</strong></p>
            <div class="inbox-otp-boxes" onPaste=${handleOtpPaste}>
              ${otp.map((digit, i) => html`
                <input
                  key=${i}
                  ref=${(el) => otpRefs.current[i] = el}
                  type="text"
                  maxLength="1"
                  inputMode="numeric"
                  pattern="[0-9]"
                  class="inbox-otp-box"
                  value=${digit}
                  onInput=${(e) => handleOtpInput(i, e.target.value)}
                  onKeyDown=${(e) => handleOtpKeyDown(i, e)}
                />
              `)}
            </div>

            <!--
              Timer + Resend row, sits directly under the 6 boxes.
              Three states:
                - secondsLeft > 0 + no cooldown : "Expires in m:ss · Resend OTP"
                - secondsLeft = 0 + no cooldown : "OTP expired · Resend OTP"
                - cooldown active                : "Resend available in m:ss" (resend disabled)
              Resend is always reachable unless the 30-min server cooldown is in effect.
            -->
            <div
              class="inbox-otp-meta"
              style="display:flex;justify-content:space-between;align-items:center;gap:0.75rem;margin:0.5rem 0 0.75rem"
            >
              <span
                class="inbox-otp-timer"
                style=${`font-size:0.82rem;${secondsLeft > 0 ? 'color:var(--text-secondary,#9aa3b2)' : 'color:var(--text-muted,#7d8799)'}`}
              >
                ${secondsLeft > 0 ? `Expires in ${formatTimer()}` : 'OTP expired'}
              </span>
              ${cooldownLeft > 0
                ? html`
                    <span class="inbox-text-muted" style="font-size:0.78rem">
                      Resend in ${formatCooldown()}
                    </span>
                  `
                : html`
                    <button
                      class="inbox-btn-link"
                      onClick=${handleSendOtp}
                      disabled=${sending}
                      style="font-size:0.82rem"
                    >
                      ${sending ? 'Sending…' : 'Resend OTP'}
                    </button>
                  `
              }
            </div>

            <button
              class="inbox-btn inbox-btn-primary"
              onClick=${() => handleVerifyOtp(otp.join(''))}
              disabled=${verifying || otp.join('').length < 6}
            >
              <span>${verifying ? 'Verifying…' : 'Verify OTP'}</span>
            </button>
            <button
              class="inbox-btn-link"
              onClick=${() => { setStep('identifier'); setOtp(['','','','','','']); setError(null); }}
              style="margin-top:0.5rem"
            >← Change identifier</button>
            ${error && html`<div class="inbox-error">${error}</div>`}
          </div>
        `}
      </div>
    </div>
  `;
}
